import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';
import { get, sortBy } from 'lodash-es';

import SelectCommon from '../Select';

import { TIMEOUT_EVENT_EMITTER } from '../common.utils';
import { PhoneDefaultPrefixEnum } from './index.enum';
import {
  INPUT_1_CLASS,
  INPUT_2_CLASS,
  addClassName,
  getCodePrefix,
  getCorrectValue,
  getFormatOptionLabelCustom,
  getFormattedValue,
  setPrefix,
} from './index.utils';
import { checkIsEmpty } from '../../Utils/utils';
import { customPhonePrefixList } from './index.service';
import { fieldShape, formatShape, phonePrefixListShape } from './index.shape';

import './index.scss';

/**
 * text field component
 * @param props
 * @returns {XML}
 * @constructor
 */
const InputPhoneCommon = ({
  field: { name, value },
  formatCustom,
  prefixCode,
  prefixValue,
  disabled,
  placeholder,
  phonePrefixList,
  showError,
  existError,
  eventEmitter,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [format, setFormat] = useState(formatCustom);
  const [localPrefixCode, setPrefixCode] = useState(prefixCode);
  const [localPrefixValue, setPrefixValue] = useState(prefixValue);
  let timout;

  /**
   * handle change format
   * @param {*} param0
   */
  const handlerChangeFormat = ({ phone }) => {
    const country = get(phone, 'country');
    if (!checkIsEmpty(country)) {
      // get metatdata country {format and maxlength}
      const metadataCountry = get(phone, `metadata.countries.${country}`);
      setFormat({ format: metadataCountry[4][0], maxlength: metadataCountry[3][0] });
    }
  };

  /**
   * handle prefix change
   * @param {*} param0
   */
  const handlerChangePrefix = ({ prefixVal }) => {
    if (!checkIsEmpty(prefixVal)) {
      const prefixCodeLocal = getCodePrefix({ list: phonePrefixList, value: prefixVal });
      const phone = parsePhoneNumberFromString(`${prefixCodeLocal}0000`);
      handlerChangeFormat({ phone });
      setPrefixValue(prefixVal);
      setPrefixCode(prefixCodeLocal);
    }
  };

  /**
   * handler change local value
   */
  const handlerChangeLocalValue = () => {
    try {
      if (!checkIsEmpty(localValue)) {
        const customValue = setPrefix(localValue);

        const phone = parsePhoneNumberFromString(customValue);
        const country = get(phone, 'country');

        handlerChangeFormat({ phone });
        handlerChangePrefix({ prefixVal: country });

        if (!checkIsEmpty(country)) {
          setLocalValue(get(phone, 'nationalNumber'));
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`${e.stack}`);
    }
  };

  // observable localValue
  useEffect(() => {
    if (timout) clearTimeout(timout);
    timout = setTimeout(
      () => eventEmitter(getCorrectValue({ value: localValue, prefixCode: localPrefixCode })),
      TIMEOUT_EVENT_EMITTER
    );
    // handler change localvalue
    handlerChangeLocalValue();

    return () => {
      clearTimeout(timout);
    };
  }, [localValue, localPrefixCode]);

  // TODO observable value
  // useEffect(() => {
  //   setLocalValue(value);
  // }, [value]);

  return (
    <div className="field-input-phone-24qsdfssfg4-00f8-4e7b-8825-dd064f4176a9">
      <SelectCommon
        field={{
          name: 'prefix',
          value: localPrefixValue,
        }}
        showError={showError}
        existError={existError}
        selection={sortBy(phonePrefixList, 'name')}
        disabled={disabled}
        width="80px"
        formatOptionLabel={getFormatOptionLabelCustom}
        eventEmitter={event => handlerChangePrefix({ prefixVal: event })}
      />
      <span className={addClassName({ inputClass: INPUT_1_CLASS })}>{localPrefixCode}</span>
      <input
        className={addClassName({ inputClass: INPUT_2_CLASS, existError, showError })}
        name={name}
        type="tel"
        value={getFormattedValue({ value: localValue, format })}
        maxLength={get(format, 'maxlength')}
        disabled={disabled}
        placeholder={placeholder}
        onChange={event => setLocalValue(get(event, 'target.value'))}
      />
    </div>
  );
};

InputPhoneCommon.propTypes = {
  field: PropTypes.shape(fieldShape),
  formatCustom: PropTypes.shape(formatShape),
  phonePrefixList: PropTypes.arrayOf(phonePrefixListShape),
  existError: PropTypes.bool,
  showError: PropTypes.bool,
  prefixCode: PropTypes.string,
  prefixValue: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  eventEmitter: PropTypes.func,
};

InputPhoneCommon.defaultProps = {
  field: {
    value: 'FR',
  },
  formatCustom: {
    format: [PhoneDefaultPrefixEnum.FORMAT_1, PhoneDefaultPrefixEnum.FORMAT_2],
    maxlength: PhoneDefaultPrefixEnum.MAX_LENGTH,
  },
  existError: false,
  showError: false,
  placeholder: '',
  disabled: false,
  phonePrefixList: customPhonePrefixList,
  prefixCode: PhoneDefaultPrefixEnum.CODE,
  prefixValue: PhoneDefaultPrefixEnum.VALUE,
  eventEmitter: () => {},
};

export default InputPhoneCommon;
